<template>
  <f7-page name="my-suggestions" @page:beforein="onPageBeforeIn">
    <f7-navbar :title="$t('item.mySuggestions.title')" back-link></f7-navbar>

    <!-- 1. FIRST READ IN FLIGHT. A skeleton, never the empty state — "you named
            nothing" must not flash before the request lands. -->
    <f7-list v-if="state === 'loading'" media-list inset class="my-suggestions__skeleton">
      <f7-list-item v-for="n in 3" :key="n">
        <template #title>
          <f7-skeleton-block style="width: 140px; height: 14px"></f7-skeleton-block>
        </template>
        <template #footer>
          <f7-skeleton-block style="width: 200px; height: 10px"></f7-skeleton-block>
        </template>
      </f7-list-item>
    </f7-list>

    <!-- 2. THE READ FAILED. 403 `not-a-member` is not retryable — only an
            approved seller member reads this — so it gets its own sentence and
            no button. -->
    <f7-block v-else-if="state === 'error'" class="text-align-center my-suggestions__error">
      <f7-icon
        f7="exclamationmark_triangle_fill"
        size="44"
        color="orange"
        class="margin-bottom-half"
      ></f7-icon>
      <p class="text-color-gray" role="alert">{{ errorMessage }}</p>
      <f7-button
        v-if="errorCode !== 'not-a-member'"
        outline
        class="margin-top-half my-suggestions__retry"
        @click="refresh"
      >
        {{ $t('item.mySuggestions.retry') }}
      </f7-button>
    </f7-block>

    <template v-else>
      <!-- THE TOTALS AND THE RULE. Printed as given: the two figures are not
           derived from each other or from the rows (a declined reward is in
           neither). The rule is two numbers off `meta` — never typed here — and
           is simply absent while the reward is off. -->
      <f7-block class="my-suggestions__summary">
        <div class="my-suggestions__figures">
          <div class="my-suggestions__figure">
            <div class="my-suggestions__figure-value my-suggestions__figure-value--earned">
              {{ amount(meta.earnedCents) }}
            </div>
            <div class="my-suggestions__figure-label">
              {{ $t('item.mySuggestions.totals.earned') }}
            </div>
          </div>
          <div class="my-suggestions__figure">
            <div class="my-suggestions__figure-value">{{ amount(meta.awaitingCents) }}</div>
            <div class="my-suggestions__figure-label">
              {{ $t('item.mySuggestions.totals.awaiting') }}
            </div>
          </div>
        </div>

        <p v-if="meta.cappedCents > 0" class="my-suggestions__capped">
          {{ $t('item.mySuggestions.totals.capped', { amount: amount(meta.cappedCents) }) }}
        </p>

        <p v-if="ruleText" class="my-suggestions__rule">{{ ruleText }}</p>
      </f7-block>

      <!-- EMPTY — says how to start, in the place she would start. -->
      <f7-block v-if="state === 'empty'" class="text-align-center my-suggestions__empty">
        <f7-icon f7="lightbulb" size="48" color="gray" class="margin-bottom-half"></f7-icon>
        <p class="my-suggestions__empty-title">{{ $t('item.mySuggestions.empty.title') }}</p>
        <p class="text-color-gray">{{ $t('item.mySuggestions.empty.body') }}</p>
      </f7-block>

      <f7-list
        v-else
        media-list
        strong-ios
        outline-ios
        dividers-ios
        inset
        class="my-suggestions__list"
      >
        <f7-list-item v-for="row in rows" :key="row.id">
          <template #inner>
            <div class="my-suggestions__row">
              <div class="my-suggestions__product">{{ productNameOf(row) }}</div>

              <div v-if="shopLineOf(row)" class="my-suggestions__meta">{{ shopLineOf(row) }}</div>

              <!-- The club's answer in her words — only once there is one. An
                   open tip's sentence repeats the shop and the date the line
                   above already carries. -->
              <div v-if="answerLineOf(row)" class="my-suggestions__answer">
                {{ answerLineOf(row) }}
              </div>

              <div class="my-suggestions__helped">
                {{ $t('item.mySuggestions.row.helped', { count: row.helpedMembers }) }}
              </div>

              <div
                v-if="row.earnedCents > 0 || row.awaitingCents > 0"
                class="my-suggestions__amounts"
              >
                <span v-if="row.earnedCents > 0" class="my-suggestions__earned">
                  {{ $t('item.mySuggestions.row.earned', { amount: amount(row.earnedCents) }) }}
                </span>
                <span v-if="row.awaitingCents > 0" class="my-suggestions__awaiting">
                  {{ $t('item.mySuggestions.row.awaiting', { amount: amount(row.awaitingCents) }) }}
                </span>
              </div>
            </div>
          </template>
        </f7-list-item>
      </f7-list>

      <f7-block v-if="meta.hasMore" class="text-align-center my-suggestions__truncated">
        <p class="text-color-gray">
          {{ $t('item.mySuggestions.truncated', { count: meta.returned }) }}
        </p>
      </f7-block>
    </template>
  </f7-page>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency';
import { MY_SUGGESTIONS_ERROR, useMySuggestions } from '@/composables/useMySuggestions.js';
import { formatAvailabilityDate } from '@/utils/productAvailability.js';
import { SUGGESTION_STATUS, suggestionLabel } from '@/utils/supplierSuggestion.js';

defineOptions({ name: 'MySuggestionsPage' });

const { t, locale } = useI18n({ useScope: 'global' });
const { formatAmount } = useCurrency();

const { rows, meta, state, errorCode, refresh } = useMySuggestions();

/** PoolShare as she reads it (1000 piasters → "10"). */
function amount(cents) {
  return formatAmount(cents, { trimZeros: true });
}

const errorMessage = computed(() =>
  errorCode.value === MY_SUGGESTIONS_ERROR.NOT_A_MEMBER
    ? t('item.mySuggestions.errors.notMember')
    : t('item.mySuggestions.errors.generic'),
);

// The rule in one sentence: both numbers come off `meta`, and with either one
// missing the sentence would have a hole in it, so it is withheld instead.
const ruleText = computed(() =>
  meta.value.rewardCents && meta.value.monthlyCapCents
    ? t('item.mySuggestions.rule', {
      amount: amount(meta.value.rewardCents),
      limit: amount(meta.value.monthlyCapCents),
    })
    : '',
);

function productNameOf(row) {
  return row.product?.name || t('item.mySuggestions.row.productGone');
}

/** "Trust · sent on 12 Oct" — the shop and the day, whatever the answer is. */
function shopLineOf(row) {
  const { suggestion } = row;
  if (!suggestion) return '';
  const date = suggestion.at ? formatAvailabilityDate(suggestion.at, locale.value) : '';
  const sent = date ? t('item.mySuggestions.row.sentOn', { date }) : '';
  return [suggestion.supplierName, sent].filter(Boolean).join(' · ');
}

// Only once the club has said something: an answer or an outcome. The open
// receipt would just repeat the shop and the date.
function answerLineOf(row) {
  const { suggestion } = row;
  if (!suggestion) return '';
  const answered = suggestion.status !== SUGGESTION_STATUS.OPEN || suggestion.outcome !== null;
  return answered ? suggestionLabel(suggestion, { t, locale: locale.value }) : '';
}

// Refetch on every page-enter (F7 fires page:beforein on first mount AND on each
// re-entry), with no polling timer. refresh() never rejects.
function onPageBeforeIn() {
  return refresh();
}
</script>

<style scoped>
.my-suggestions__error,
.my-suggestions__empty {
  padding-block: 32px;
}

.my-suggestions__skeleton {
  pointer-events: none;
}

.my-suggestions__figures {
  display: flex;
  gap: 12px;
}

.my-suggestions__figure {
  flex: 1;
  padding: 12px;
  border-radius: var(--poolit-radius-md);
  background: var(--poolit-surface, #fff);
  border: 1px solid var(--poolit-border, #e2e5e3);
  text-align: center;
}

.my-suggestions__figure-value {
  font-size: 24px;
  font-weight: var(--poolit-font-weight-bold, 700);
  line-height: 1.2;
  color: var(--poolit-text-primary);
}

.my-suggestions__figure-value--earned {
  color: var(--ci-ok-fg, #1f6b45);
}

.my-suggestions__figure-label {
  margin-top: 4px;
  font-size: var(--poolit-font-size-xs);
  line-height: 1.4;
  color: var(--poolit-text-muted);
}

.my-suggestions__capped,
.my-suggestions__rule {
  margin: 10px 0 0;
  font-size: var(--poolit-font-size-sm);
  line-height: 1.55;
  color: var(--poolit-text-secondary);
}

.my-suggestions__empty-title {
  margin: 0 0 4px;
  font-weight: var(--poolit-font-weight-medium);
  color: var(--poolit-text-primary);
}

.my-suggestions__row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  padding-block: 8px;
}

.my-suggestions__product {
  font-weight: var(--poolit-font-weight-medium);
  color: var(--poolit-text-primary);
}

.my-suggestions__meta,
.my-suggestions__answer,
.my-suggestions__helped {
  font-size: var(--poolit-font-size-sm);
  line-height: 1.5;
  color: var(--poolit-text-secondary);
}

.my-suggestions__amounts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  font-size: var(--poolit-font-size-sm);
  font-weight: var(--poolit-font-weight-medium);
}

.my-suggestions__earned {
  color: var(--ci-ok-fg, #1f6b45);
}

.my-suggestions__awaiting {
  color: var(--ci-await-fg, #8a5300);
}
</style>
